import { DOCUMENT } from '@angular/common';
import { inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { CrossOrigin, ReferrerPolicy, ScriptInit, ScriptSpec } from '@shared/services/head-script-loader/model';
import { PlatformContextService } from '@shared/services/platform-context';

@Injectable({ providedIn: 'root' })
export class HeadScriptLoaderService {
  private readonly document: Document = inject(DOCUMENT);
  private readonly renderer: Renderer2 = inject(RendererFactory2).createRenderer(null, null);
  private readonly isServer: boolean = inject(PlatformContextService).isServer();

  /**
   * Injects multiple scripts into the current document based on the provided specifications.
   *
   * This method processes an array of script specifications, creates and appends
   * corresponding `<script>` elements to the DOM for each specification, and returns the
   * collection of successfully injected script elements. If called on the server or if no scripts
   * are provided, it returns an empty array.
   *
   * @param {readonly ScriptSpec[]} scripts - An array of script specifications, where each
   * specification contains details required to create and inject a `<script>` element.
   * The array must be immutable (`readonly`) and can contain multiple entries.
   *
   * @returns {HTMLScriptElement[]} An array of the `<script>` elements that were successfully
   * injected into the document. If no scripts were injected (e.g., running on the server or an
   * empty input), this returns an empty array.
   *
   * @example
   * ```typescript
   * const scripts: readonly ScriptSpec[] = [
   *   { src: 'https://example.com/script1.js', async: true },
   *   { src: 'https://example.com/script2.js' }
   * ];
   *
   * const injectedScripts = injectScripts(scripts);
   * console.log(injectedScripts); // Logs the array of injected <script> elements
   * ```
   *
   * @remarks
   * - This method depends on the presence of the method `injectScript` that handles individual
   * script injection, and its behavior should be well-understood before using this method.
   * - The method performs no operations and returns an empty array if executed in a server-side
   * environment (`this.isServer === true`).
   */
  injectScripts(scripts: readonly ScriptSpec[]): HTMLScriptElement[] {
    if (this.isServer || scripts.length === 0) return [];
    const out: HTMLScriptElement[] = [];
    for (const spec of scripts) {
      const el = this.injectScript(spec);
      if (el) out.push(el);
    }
    return out;
  }

  /**
   * Injects a script element into the document's `<head>` section, based on the provided specifications.
   * If a matching script element already exists in the document, the existing element will be returned
   * instead of creating a new one.
   *
   * @param {ScriptSpec} spec - The specification object that defines the properties of the script, such as `src`, `async`, `defer`, and others.
   * - `spec.src` (string | URL): The source URL of the script. Can be a string or a `URL` object.
   * - `spec.async` (boolean, optional): Indicates if the script should be executed asynchronously.
   * - `spec.defer` (boolean, optional): Indicates if the script should be executed after the document has been parsed.
   * - `spec.id` (string, optional): An optional unique identifier for the script element.
   * - `spec.type` (string, optional): The MIME type of the script. Default is `"text/javascript"`.
   * - `spec.nonce` (string, optional): A cryptographic nonce for Content Security Policy (CSP).
   * - `spec.integrity` (string, optional): An integrity hash that ensures the script hasn’t been tampered with.
   * - `spec.crossOrigin` ("anonymous" | "use-credentials" | undefined, optional): Sets the CORS setting for the script.
   * - `spec.referrerPolicy` (string, optional): The referrer policy for the script request.
   * - `spec.text` (string, optional): Inline JavaScript code to be executed (ignored if `src` is provided).
   *
   * @returns {HTMLScriptElement | null}
   * The newly created script element, or an existing script element if a match is found.
   * Returns `null` if the method is invoked in a server-side environment.
   *
   * @example
   * // Inject a script with a URL source and asynchronous execution
   * const scriptSpec: ScriptSpec = {
   *   src: 'https://example.com/script.js',
   *   async: true,
   *   id: 'example-script'
   * };
   * const scriptElement = injectScript(scriptSpec);
   * console.log(scriptElement); // Outputs an HTMLScriptElement if successful
   *
   * @remarks
   * - This method is not intended to run in server-side rendering (SSR) environments; it will return `null` in such cases.
   * - The method ensures the given script attributes are sanitized before injecting the script element.
   * - If a script with matching attributes (e.g., `src` or `id`) already exists, it prevents duplication by returning the existing element.
   * - The `text` property is ignored by the browser if a `src` is also provided, per the HTML specification.
   */
  injectScript(spec: ScriptSpec): HTMLScriptElement | null {
    if (this.isServer) return null;

    const safe = this.sanitizeScriptInit(spec);
    const existing = this.findExistingScript(safe);
    if (existing) return existing;

    const el = this.renderer.createElement('script') as HTMLScriptElement;

    if (safe.async) el.async = true;
    if (safe.defer) el.defer = true;
    if (safe.id) el.id = safe.id;
    if (safe.type) el.type = safe.type;
    if (safe.nonce) el.nonce = safe.nonce;
    if (safe.integrity) el.integrity = safe.integrity;
    if (safe.referrerPolicy) el.referrerPolicy = safe.referrerPolicy;
    if (safe.crossOrigin !== undefined) el.crossOrigin = safe.crossOrigin;

    // src can be string or URL
    if (safe.src) el.src = typeof safe.src === 'string' ? safe.src : safe.src.toString();

    // inline text content (ignored by spec if src is set, but harmless) ---
    if (safe.text) el.text = safe.text;

    const raw = spec as Record<string, unknown>;
    Object.keys(raw).forEach((k) => {
      if (k.startsWith('data-')) {
        const v = raw[k];
        if (typeof v === 'string') el.setAttribute(k, v);
      }
    });

    this.renderer.appendChild(this.document.head, el);
    return el;
  }

  /**
   * Try to find an existing script, prioritizing `id`, then normalized `src`.
   */
  private findExistingScript(safe: Pick<ScriptInit, 'id' | 'src'>): HTMLScriptElement | null {
    // Fast path by id (unique by spec)
    if (safe.id) {
      const byId = this.document.getElementById(safe.id);
      if (byId && byId.tagName === 'SCRIPT') return byId as HTMLScriptElement;
    }

    // Fallback: match by normalized absolute href of src
    const href = this.normalizeSrc(safe.src);
    if (!href) return null;

    const scripts = this.document.head.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; i++) {
      const el = scripts.item(i)!;
      // el.src is already absolute when present; fallback to raw attribute if needed
      if ((el.src && el.src === href) || el.getAttribute('src') === href) {
        return el;
      }
    }
    return null;
  }

  /**
   * Normalize a src input (string|URL|undefined) to an absolute href when possible.
   * If parsing fails, return the raw string (best-effort dedup).
   */
  private normalizeSrc(src?: string | URL): string | null {
    if (!src) return null;
    const raw = typeof src === 'string' ? src : src.toString();
    try {
      return new URL(raw, this.document.baseURI).href;
    } catch {
      return raw; // keep raw when malformed (still useful for direct attribute comparison)
    }
  }

  /**
   * Sanitize any input into a safe, precise ScriptInit shape.
   * Only own properties are considered; invalid types are discarded.
   */
  private sanitizeScriptInit(input: unknown): ScriptInit {
    // Guard non-objects / arrays / null
    const obj = input && typeof input === 'object' && !Array.isArray(input) ? (input as Record<string, unknown>) : {};

    // Local safe readers (own properties only)
    const hasOwn = (k: string): boolean => Object.hasOwn(obj, k);
    const str = (k: string): string | undefined => (hasOwn(k) && typeof obj[k] === 'string' ? obj[k] : undefined);

    // Validate union-like values
    const asReferrerPolicy = (): ReferrerPolicy | undefined => {
      const v = str('referrerPolicy');
      const allowed: readonly ReferrerPolicy[] = [
        'no-referrer',
        'no-referrer-when-downgrade',
        'origin',
        'origin-when-cross-origin',
        'same-origin',
        'strict-origin',
        'strict-origin-when-cross-origin',
        'unsafe-url',
      ];
      return v && (allowed as readonly string[]).includes(v) ? (v as ReferrerPolicy) : undefined;
    };

    const asCrossOrigin = (): CrossOrigin | undefined => {
      const v = str('crossOrigin');
      const allowed: readonly CrossOrigin[] = ['', 'anonymous', 'use-credentials'];
      return v && (allowed as readonly string[]).includes(v) ? (v as CrossOrigin) : undefined;
    };

    // src may be string or URL
    const asSrc = (): string | URL | undefined => {
      if (!hasOwn('src')) return undefined;
      const v = obj['src'];
      if (typeof v === 'string') return v;
      if (v instanceof URL) return v;
      return undefined;
    };

    // Only propagate presence-true for boolean attributes
    const asTrue = (k: 'async' | 'defer'): true | undefined => (hasOwn(k) && obj[k] === true ? true : undefined);

    // Build the result with only valid keys; omit undefined to keep the object tight
    return Object.freeze({
      id: str('id'),
      type: str('type'),
      nonce: str('nonce'),
      integrity: str('integrity'),
      referrerPolicy: asReferrerPolicy(),
      crossOrigin: asCrossOrigin(),
      src: asSrc(),
      async: asTrue('async'),
      defer: asTrue('defer'),
      text: str('text'),
    });
  }
}
